import { useEffect, useState } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  Banner,
  Box,
  Badge,
  Button,
  Spinner,
  Icon,
  TextField,
} from "@shopify/polaris";
import { CheckCircleIcon, AlertCircleIcon } from "@shopify/polaris-icons";
import supabase from "../../supabase-client";
import { formatPrice, formatDate } from "../../utility/funcs";
import {
  previewClipRefund,
  requestClipRefund,
  waitForClipRefund,
  resolveClipRefund,
  messageFor,
} from "../../utility/pagos";

// Returning a deposit that was CHARGED through Clip: a refund to the card that
// paid, through pagos-svc.
//
// A Clip deposit is a sale, not a hold. Marking it "returned" without a refund
// moves no money — that is how 1485, 1561 and 1603 were "returned" while the
// customer was still owed. So for these deposits the refund is the only way.
//
// Clip only refunds while the day's Clip sales cover it. When they do not, the
// refund waits ("Esperando fondos") and sends itself on the next sale —
// nobody has to come back to it.

// Off: card deposits go back by SPEI instead (CardDepositReturn.js), because a
// refund waits until the day's Clip sales cover it. Kept for when Clip can
// fund refunds from the settled balance; pagos-svc has its own switch
// (CLIP_REFUNDS_ENABLED) and refuses new refunds while it is off.
export const CLIP_REFUNDS_ENABLED = false;

const money = (c) => formatPrice(c / 100);

// Deposit charged through Clip for this reservation, net of refunds, and the
// charge carrying the most of it (the one a refund would reference).
export async function loadClipDepositSource(reservationId) {
  const { data: apps } = await supabase
    .from("payment_applications")
    .select("amount_cents, payment:payments(id, direction, rail, source, external_id, card_last4, card_brand, occurred_at)")
    .eq("reservation_id", reservationId)
    .eq("purpose", "deposit");

  let net = 0;
  let origin = null;
  const byPayment = {};
  for (const a of apps || []) {
    const p = a.payment;
    if (!p || p.source !== "clip" || p.rail !== "card") continue;
    const c = Number(a.amount_cents);
    net += p.direction === "in" ? c : -c;
    if (p.direction !== "in") continue;
    byPayment[p.id] = byPayment[p.id] || { payment: p, cents: 0 };
    byPayment[p.id].cents += c;
  }
  for (const x of Object.values(byPayment)) if (!origin || x.cents > origin.cents) origin = x;
  if (net <= 0 || !origin) return null;
  return { cents: net, maxCents: Math.min(net, origin.cents), payment: origin.payment };
}

// The latest refund request for the reservation, if any.
export async function loadClipRefund(reservationId) {
  const { data } = await supabase
    .from("clip_refunds")
    .select("id, status, amount_cents, receipt_no, error_code, last_error, created_at, last_attempt_at, resolved_at, clip_receipt_no")
    .eq("reservation_id", reservationId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data || null;
}

export const clipRefundOpen = (r) => !!r && ["queued", "awaiting_funds", "unknown"].includes(r.status);

const cardLabel = (p) =>
  [p?.card_brand ? p.card_brand.toUpperCase() : null, p?.card_last4 ? `···${p.card_last4}` : null]
    .filter(Boolean)
    .join(" ") || "la tarjeta";

// Confirmation step, after the Devolver Depósito modal closes. The amount shown
// is the server's, never one the browser worked out.
export function ConfirmClipRefundModal({ open, reservationId, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("confirm"); // confirm | sending | succeeded | awaiting_funds | unknown | pending
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    setPreview(null);
    setError(null);
    setStep("confirm");
    setResult(null);
    let cancelled = false;
    (async () => {
      try {
        const p = await previewClipRefund(reservationId);
        if (cancelled) return;
        setPreview(p);
        if (p.blocked) setError(messageFor(p.blocked.code, p.blocked.message));
      } catch (err) {
        if (!cancelled) setError(err.message || "No se pudo consultar el reembolso.");
      }
    })();
    return () => { cancelled = true; };
  }, [open, reservationId]);

  const send = async () => {
    setError(null);
    setStep("sending");
    try {
      const { id } = await requestClipRefund(reservationId);
      const final = await waitForClipRefund(id);
      setResult(final);
      if (final.status === "succeeded") return setStep("succeeded");
      if (final.status === "awaiting_funds") return setStep("awaiting_funds");
      if (final.status === "unknown") return setStep("unknown");
      if (final.status === "pending_timeout" || final.status === "queued") return setStep("pending");
      setStep("confirm");
      setError(final.last_error || "Clip rechazó el reembolso.");
    } catch (err) {
      setStep("confirm");
      setError(err.message || "No se pudo solicitar el reembolso.");
    }
  };

  const ready = preview && !preview.blocked && preview.amount_cents > 0;
  const done = !["confirm", "sending"].includes(step);
  const shown = preview?.amount_cents ?? 0;

  return (
    <Modal
      open={open}
      onClose={step === "sending" ? () => {} : done ? () => onDone?.() : onClose}
      title={step === "confirm" ? "Confirma la devolución" : "Devolución de depósito"}
      primaryAction={
        step === "confirm"
          ? { content: `Devolver ${money(shown)}`, onAction: send, disabled: !ready, destructive: true }
          : done
          ? { content: "Cerrar", onAction: () => onDone?.() }
          : undefined
      }
      secondaryActions={step === "confirm" ? [{ content: "Regresar", onAction: onClose }] : []}
    >
      <Modal.Section>
        {step === "confirm" ? (
          <BlockStack gap="400">
            {error && <Banner tone="critical">{error}</Banner>}
            <BlockStack gap="100" inlineAlign="center">
              <Text tone="subdued">Se devolverá</Text>
              <Text variant="heading2xl" as="p" numeric>{preview ? money(shown) : "…"}</Text>
              <Text tone="subdued" variant="bodySm">Depósito de la reservación #{reservationId}</Text>
            </BlockStack>
            {preview && (
              <Box background="bg-surface-secondary" borderRadius="200" padding="400">
                <BlockStack gap="300">
                  <Row label="Tarjeta" value={cardLabel(preview)} />
                  <Row label="Cobro Clip" value={`${preview.receipt_no} · ${money(preview.charge_cents)}`} />
                </BlockStack>
              </Box>
            )}
            <Text tone="subdued" variant="bodySm">
              Regresa a la tarjeta con la que se pagó. Al cliente le aparece en unos días hábiles.
            </Text>
          </BlockStack>
        ) : (
          <BlockStack gap="400" inlineAlign="center">
            {step === "sending" ? (
              <Spinner size="large" />
            ) : step === "succeeded" ? (
              <Icon source={CheckCircleIcon} tone="success" />
            ) : (
              <Icon source={AlertCircleIcon} tone="caution" />
            )}
            <Text variant="headingMd" as="p">
              {{
                sending: "Solicitando a Clip…",
                succeeded: "¡Listo!",
                awaiting_funds: "Esperando fondos",
                unknown: "Hay que revisar en Clip",
                pending: "Sigue en proceso",
              }[step]}
            </Text>
            <Text tone="subdued" numeric>{money(shown)} a {cardLabel(preview)}</Text>
            {step === "awaiting_funds" && (
              <Banner tone="warning">
                Las ventas Clip de hoy aún no alcanzan para el reembolso. Quedó solicitado y se
                envía solo con la siguiente venta Clip — no hay que volver a capturarlo.
              </Banner>
            )}
            {step === "unknown" && (
              <Banner tone="critical">
                Clip no confirmó si se hizo ({result?.last_error}). No lo vuelvas a enviar: revisa el
                cobro {preview?.receipt_no} en el dashboard de Clip.
              </Banner>
            )}
            {step === "pending" && (
              <Banner tone="info">
                Clip aún no responde. El depósito se marcará como devuelto en cuanto confirme.
              </Banner>
            )}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

// One line on the Depósito card while a refund is open (or after it went out).
// Detail only when something needs a person.
export function ClipRefundStatus({ refund, canResolve, onChanged }) {
  const [resolving, setResolving] = useState(null); // outcome being confirmed
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!refund || refund.status === "canceled" || refund.status === "failed") return null;

  const badge = {
    queued: <Badge tone="info">Enviando a Clip</Badge>,
    awaiting_funds: <Badge tone="warning">Esperando fondos</Badge>,
    unknown: <Badge tone="critical">Revisar en Clip</Badge>,
    succeeded: <Badge tone="success">Devuelto</Badge>,
  }[refund.status];

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await resolveClipRefund(refund.id, resolving, note);
      setResolving(null);
      setNote("");
      onChanged?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <BlockStack gap="200">
      <InlineGrid columns="1fr auto" alignItems="center">
        <Text variant="bodySm" tone="subdued">
          Devolución {money(Number(refund.amount_cents))}
          {refund.status === "succeeded" && refund.resolved_at
            ? ` · ${formatDate(refund.resolved_at.substring(0, 10))}`
            : ""}
        </Text>
        {badge}
      </InlineGrid>
      {refund.status === "unknown" && (
        <BlockStack gap="200">
          <Text variant="bodySm" tone="critical">
            Clip no confirmó si se hizo. Revisa el cobro {refund.receipt_no} en el dashboard de Clip.
          </Text>
          {canResolve && !resolving && (
            <InlineStack gap="200">
              <Button size="slim" onClick={() => setResolving("refunded")}>Sí se reembolsó</Button>
              <Button size="slim" onClick={() => setResolving("not_refunded")}>No se hizo, reintentar</Button>
            </InlineStack>
          )}
        </BlockStack>
      )}
      {refund.status === "awaiting_funds" && canResolve && !resolving && (
        <InlineStack>
          <Button size="slim" variant="plain" onClick={() => setResolving("cancel")}>Cancelar reembolso</Button>
        </InlineStack>
      )}
      {resolving && (
        <BlockStack gap="200">
          <TextField
            label={resolving === "cancel" ? "Motivo" : "Qué se verificó en Clip"}
            value={note}
            onChange={setNote}
            autoComplete="off"
            error={err || undefined}
          />
          <InlineStack gap="200">
            <Button size="slim" variant="primary" loading={busy} disabled={note.trim().length < 5} onClick={submit}>
              Confirmar
            </Button>
            <Button size="slim" onClick={() => { setResolving(null); setErr(null); }}>Regresar</Button>
          </InlineStack>
        </BlockStack>
      )}
    </BlockStack>
  );
}

function Row({ label, value }) {
  return (
    <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
      <Text tone="subdued">{label}</Text>
      <Text as="span" fontWeight="semibold">{value}</Text>
    </InlineStack>
  );
}
